import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Search,
  Star,
  ChevronRight,
  Crown,
} from "lucide-react";

// Real coin logos from CoinGecko's CDN
const coinList = [
  { symbol: "BTCUSDT", pair: "BTC/USDT", leverage: "10x", color: "#F7931A", logo: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png" },
  { symbol: "ETHUSDT", pair: "ETH/USDT", leverage: "10x", color: "#8A92B2", logo: "https://assets.coingecko.com/coins/images/279/small/ethereum.png" },
  { symbol: "LTCUSDT", pair: "LTC/USDT", leverage: "5x", color: "#BFBBBB", logo: "https://assets.coingecko.com/coins/images/2/small/litecoin.png" },
  { symbol: "BNBUSDT", pair: "BNB/USDT", leverage: "5x", color: "#F3BA2F", logo: "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png" },
  { symbol: "NEOUSDT", pair: "NEO/USDT", leverage: "3x", color: "#58BF00", logo: "https://assets.coingecko.com/coins/images/480/small/NEO_512_512.png" },
  { symbol: "QTUMUSDT", pair: "QTUM/USDT", leverage: "3x", color: "#2E9AD0", logo: "https://assets.coingecko.com/coins/images/965/small/qtum.png" },
  { symbol: "EOSUSDT", pair: "EOS/USDT", leverage: "3x", color: "#443F54", logo: "https://assets.coingecko.com/coins/images/738/small/eos-symbol.png" },
  { symbol: "SNTUSDT", pair: "SNT/USDT", leverage: "3x", color: "#FF7D51", logo: "https://assets.coingecko.com/coins/images/776/small/status.png" },
  { symbol: "BNTUSDT", pair: "BNT/USDT", leverage: "3x", color: "#1D2B4F", logo: "https://assets.coingecko.com/coins/images/1064/small/bancor.png" },
  { symbol: "BCHUSDT", pair: "BCH/USDT", leverage: "5x", color: "#8DC351", logo: "https://assets.coingecko.com/coins/images/780/small/bitcoin-cash-circle.png" },
  { symbol: "GASUSDT", pair: "GAS/USDT", leverage: "3x", color: "#63C7B2", logo: "https://assets.coingecko.com/coins/images/935/small/gas.png" },
  { symbol: "PAXGUSDT", pair: "XAU/USDT", leverage: "5x", color: "#FFD700", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d7/Gold_icon_with_circle.svg/1200px-Gold_icon_with_circle.svg.png" },
];

function formatVolume(n) {
  if (!n && n !== 0) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (abs >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (abs >= 1e3) return (n / 1e3).toFixed(2) + "K";
  return n.toFixed(2);
}

function formatPrice(n) {
  if (!n && n !== 0) return "0.0000";
  if (n >= 100) return n.toFixed(2);
  return n.toFixed(4);
}

const initialRows = coinList.map((c) => ({
  ...c,
  price: 0,
  changePct: 0,
  volume: 0,
  high: 0,
  low: 0,
  open: 0,
  baseVolume: 0,
}));

const FIELD = { NAME: "name", PRICE: "price", CHANGE: "changePct", VOLUME: "volume" };
const FILTERS = [
  { key: "all", label: "All" },
  { key: "gainers", label: "Gainers" },
  { key: "losers", label: "Losers" },
  { key: "favorites", label: "Favorites" },
];

function SortArrow({ active, direction }) {
  const color = active ? "#60a5fa" : "#475569";
  if (!active) return <ChevronsUpDown size={12} color={color} />;
  return direction === "asc" ? (
    <ChevronUp size={12} color={color} />
  ) : (
    <ChevronDown size={12} color={color} />
  );
}

// Real coin logo component using the CDN URL
function CoinIcon({ coin, size = 28 }) {
  const [imgError, setImgError] = useState(false);
  
  // Fallback to SVG monogram if image fails to load
  if (imgError) {
    const gradId = `grad-${coin.symbol}`;
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={coin.color} stopOpacity="0.95" />
            <stop offset="100%" stopColor={coin.color} stopOpacity="0.55" />
          </linearGradient>
        </defs>
        <circle cx="16" cy="16" r="15" fill={`url(#${gradId})`} stroke={coin.color} strokeOpacity="0.5" />
        <text
          x="16"
          y="21"
          textAnchor="middle"
          fontSize="14"
          fontWeight="700"
          fill="#0b1220"
        >
          {coin.symbol === "PAXGUSDT" ? "X" : coin.symbol.charAt(0)}
        </text>
      </svg>
    );
  }

  return (
    <img
      src={coin.logo}
      alt={coin.symbol === "PAXGUSDT" ? "XAU" : coin.symbol}
      width={size}
      height={size}
      style={{
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
      onError={() => setImgError(true)}
    />
  );
}

// Tiny live sparkline built from the accumulated price-history buffer
function Sparkline({ points, positive }) {
  const width = 40;
  const height = 18;
  if (!points || points.length < 2) {
    return <svg width={width} height={height} />;
  }
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const coords = points
    .map((p, i) => `${(i * step).toFixed(1)},${(height - ((p - min) / range) * height).toFixed(1)}`)
    .join(" ");
  const color = positive ? "#4ade80" : "#f87171";

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline points={coords} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const Markets = () => {
  const [activeTab, setActiveTab] = useState("coin");
  const [rows, setRows] = useState(initialRows);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [loaded, setLoaded] = useState(false);
  const [flashMap, setFlashMap] = useState({});
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [favorites, setFavorites] = useState(() => new Set());
  const [expanded, setExpanded] = useState(null);
  const [priceHistory, setPriceHistory] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const prevPrices = useRef({});
  const historyRef = useRef({});

  const fetchPrices = async () => {
    try {
      const res = await fetch("https://data-api.binance.vision/api/v3/ticker/24hr");
      const data = await res.json();
      const bySymbol = {};
      data.forEach((d) => (bySymbol[d.symbol] = d));

      const flashes = {};
      const updated = coinList.map((coin) => {
        const d = bySymbol[coin.symbol];
        const price = d ? parseFloat(d.lastPrice) : 0;
        const changePct = d ? parseFloat(d.priceChangePercent) : 0;
        const volume = d ? parseFloat(d.quoteVolume) : 0;
        const baseVolume = d ? parseFloat(d.volume) : 0;
        const high = d ? parseFloat(d.highPrice) : 0;
        const low = d ? parseFloat(d.lowPrice) : 0;
        const open = d ? parseFloat(d.openPrice) : 0;

        const prev = prevPrices.current[coin.symbol];
        if (prev !== undefined && price !== prev) {
          flashes[coin.symbol] = price > prev ? "up" : "down";
        }
        prevPrices.current[coin.symbol] = price;

        const buf = historyRef.current[coin.symbol] || [];
        const nextBuf = price ? [...buf, price].slice(-24) : buf;
        historyRef.current[coin.symbol] = nextBuf;

        return { ...coin, price, changePct, volume, baseVolume, high, low, open };
      });

      setRows(updated);
      setFlashMap(flashes);
      setPriceHistory({ ...historyRef.current });
      setLoaded(true);
      setLastUpdated(Date.now());
      setTimeout(() => setFlashMap({}), 700);
    } catch (err) {
      console.error("Failed to fetch Binance prices:", err);
    }
  };

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const tick = setInterval(() => {
      if (lastUpdated) setSecondsAgo(Math.floor((Date.now() - lastUpdated) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [lastUpdated]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const toggleFavorite = (symbol) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
  };

  const summary = useMemo(() => {
    if (!loaded) return null;
    const totalVolume = rows.reduce((sum, r) => sum + r.volume, 0);
    const gainers = rows.filter((r) => r.changePct >= 0).length;
    const losers = rows.length - gainers;
    const topMover = [...rows].sort((a, b) => Math.abs(b.changePct) - Math.abs(a.changePct))[0];
    const topGainer = [...rows].sort((a, b) => b.changePct - a.changePct)[0];
    const topLoser = [...rows].sort((a, b) => a.changePct - b.changePct)[0];
    return { totalVolume, gainers, losers, topMover, topGainer, topLoser };
  }, [rows, loaded]);

  const visibleRows = useMemo(() => {
    let list = [...rows];

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((r) => r.pair.toLowerCase().includes(q) || r.symbol.toLowerCase().includes(q));
    }

    if (filter === "gainers") list = list.filter((r) => r.changePct > 0);
    else if (filter === "losers") list = list.filter((r) => r.changePct < 0);
    else if (filter === "favorites") list = list.filter((r) => favorites.has(r.symbol));

    if (sortField) {
      list.sort((a, b) => {
        if (sortField === FIELD.NAME) {
          return sortDir === "asc" ? a.pair.localeCompare(b.pair) : b.pair.localeCompare(a.pair);
        }
        const av = a[sortField];
        const bv = b[sortField];
        return sortDir === "asc" ? av - bv : bv - av;
      });
    }

    return list;
  }, [rows, query, filter, favorites, sortField, sortDir]);

  // Improved grid style for mobile responsiveness
  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(140px,1.8fr) minmax(80px,1fr) minmax(65px,0.8fr)",
    alignItems: "center",
    columnGap: "6px",
    width: "100%",
  };

  const sortBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    fontSize: "11px",
    fontWeight: 600,
    padding: 0,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900 text-white font-sans">
      <div className="flex items-center justify-center px-4 py-3 -mt-6 border-b border-blue-900">
        <h1 className="text-base font-medium">Transaction Quote</h1>
      </div>

      <div className="flex flex-col items-center pt-2 pb-2">
        <div className="w-full max-w-3xl space-y-3">
          <div className="flex bg-gray-800 rounded-full p-1 w-full max-w-lg mx-auto shadow-inner">
            <button
              onClick={() => setActiveTab("optional")}
              className={`flex-1 px-8 py-1 rounded-full text-sm font-medium transition ${
                activeTab === "optional"
                  ? "bg-gradient-to-r from-blue-400 to-blue-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Optional
            </button>
            <button
              onClick={() => setActiveTab("coin")}
              className={`flex-1 px-8 py-1 rounded-full text-sm font-medium transition ${
                activeTab === "coin"
                  ? "bg-gradient-to-r from-blue-400 to-blue-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Coin
            </button>
          </div>

          {activeTab === "coin" && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "6px", padding: "0 8px" }}>
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  borderRadius: "9999px",
                  backgroundColor: "#4ade80",
                  boxShadow: "0 0 0 0 rgba(74,222,128,0.6)",
                  animation: loaded ? "marketsPulse 1.6s infinite" : "none",
                }}
              />
              <span style={{ fontSize: "10px", color: "#64748b" }}>
                {loaded ? `Live · updated ${secondsAgo}s ago` : "Connecting…"}
              </span>
              <style>{`
                @keyframes marketsPulse {
                  0% { box-shadow: 0 0 0 0 rgba(74,222,128,0.55); }
                  70% { box-shadow: 0 0 0 6px rgba(74,222,128,0); }
                  100% { box-shadow: 0 0 0 0 rgba(74,222,128,0); }
                }
              `}</style>
            </div>
          )}

          {activeTab === "coin" && (
            <div style={{ padding: "0 4px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "8px",
                  padding: "12px",
                  marginBottom: "10px",
                  borderRadius: "12px",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div>
                  <div style={{ fontSize: "10px", color: "#64748b", marginBottom: "2px" }}>24h Volume</div>
                  <div style={{ fontSize: "13px", fontWeight: 700 }}>
                    {summary ? `$${formatVolume(summary.totalVolume)}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "10px", color: "#64748b", marginBottom: "2px" }}>Gainers / Losers</div>
                  <div style={{ fontSize: "13px", fontWeight: 700 }}>
                    <span style={{ color: "#4ade80" }}>{summary ? summary.gainers : "—"}</span>
                    {" / "}
                    <span style={{ color: "#f87171" }}>{summary ? summary.losers : "—"}</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "10px", color: "#64748b", marginBottom: "2px" }}>Top Mover</div>
                  <div style={{ fontSize: "13px", fontWeight: 700 }}>
                    {summary && summary.topMover ? summary.topMover.pair.split("/")[0] : "—"}
                    {summary && summary.topMover && (
                      <span style={{ color: summary.topMover.changePct >= 0 ? "#4ade80" : "#f87171", marginLeft: "4px" }}>
                        {summary.topMover.changePct >= 0 ? "+" : ""}
                        {summary.topMover.changePct.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  marginBottom: "10px",
                  borderRadius: "9999px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                }}
              >
                <Search size={14} color="#64748b" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search coin or pair"
                  style={{
                    background: "none",
                    border: "none",
                    outline: "none",
                    color: "#ffffff",
                    fontSize: "13px",
                    width: "100%",
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: "6px", marginBottom: "10px", overflowX: "auto" }}>
                {FILTERS.map((f) => {
                  const active = filter === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key)}
                      style={{
                        flexShrink: 0,
                        padding: "6px 14px",
                        borderRadius: "9999px",
                        fontSize: "12px",
                        fontWeight: 600,
                        border: "1px solid " + (active ? "transparent" : "rgba(255,255,255,0.08)"),
                        background: active ? "linear-gradient(to right, #60a5fa, #2563eb)" : "transparent",
                        color: active ? "#ffffff" : "#94a3b8",
                        cursor: "pointer",
                      }}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>

              <div
                style={{
                  ...gridStyle,
                  padding: "0 4px 10px",
                  marginBottom: "4px",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <button style={sortBtnStyle}>Name / Vol</button>
                <button
                  style={{ ...sortBtnStyle, justifyContent: "flex-end" }}
                  onClick={() => handleSort(FIELD.PRICE)}
                >
                  Price
                  <SortArrow active={sortField === FIELD.PRICE} direction={sortDir} />
                </button>
                <button
                  style={{ ...sortBtnStyle, justifyContent: "flex-end", paddingRight: "2px" }}
                  onClick={() => handleSort(FIELD.CHANGE)}
                >
                  Chg
                  <SortArrow active={sortField === FIELD.CHANGE} direction={sortDir} />
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                {visibleRows.length === 0 && (
                  <div style={{ textAlign: "center", color: "#64748b", fontSize: "13px", padding: "24px 0" }}>
                    No pairs match your filter.
                  </div>
                )}

                {visibleRows.map((coin) => {
                  const isUp = coin.changePct >= 0;
                  const flash = flashMap[coin.symbol];
                  const rowBg =
                    flash === "up"
                      ? "rgba(34,197,94,0.10)"
                      : flash === "down"
                      ? "rgba(239,68,68,0.10)"
                      : "transparent";
                  const isExpanded = expanded === coin.symbol;
                  const isFav = favorites.has(coin.symbol);
                  const isTopGainer = summary && summary.topGainer && summary.topGainer.symbol === coin.symbol && coin.changePct > 0;
                  const isTopLoser = summary && summary.topLoser && summary.topLoser.symbol === coin.symbol && coin.changePct < 0;
                  const history = priceHistory[coin.symbol];

                  const range = coin.high - coin.low;
                  const rangePct = range > 0 ? Math.min(100, Math.max(0, ((coin.price - coin.low) / range) * 100)) : 50;

                  return (
                    <div key={coin.symbol}>
                      <div
                        onClick={() => setExpanded(isExpanded ? null : coin.symbol)}
                        style={{
                          ...gridStyle,
                          padding: "8px 4px",
                          borderRadius: "10px",
                          backgroundColor: rowBg,
                          transition: "background-color 0.3s ease",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "4px", 
                          minWidth: 0, 
                          overflow: "hidden" 
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(coin.symbol);
                            }}
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}
                          >
                            <Star
                              size={12}
                              color={isFav ? "#fbbf24" : "#475569"}
                              fill={isFav ? "#fbbf24" : "none"}
                            />
                          </button>
                          <CoinIcon coin={coin} size={24} />
                          <div style={{ minWidth: 0, overflow: "hidden" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#ffffff",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "block",
                                  maxWidth: "100%",
                                }}
                              >
                                {coin.pair.split("/")[0]}
                                <span style={{ color: "#64748b" }}>/{coin.pair.split("/")[1]}</span>
                              </span>
                              <span
                                style={{
                                  flexShrink: 0,
                                  fontSize: 9,
                                  fontWeight: 700,
                                  color: "#fbbf24",
                                  backgroundColor: "rgba(251,191,36,0.12)",
                                  padding: "1px 4px",
                                  borderRadius: "4px",
                                }}
                              >
                                {coin.leverage}
                              </span>
                              {(isTopGainer || isTopLoser) && (
                                <Crown
                                  size={10}
                                  color={isTopGainer ? "#4ade80" : "#f87171"}
                                  fill={isTopGainer ? "#4ade80" : "#f87171"}
                                  style={{ flexShrink: 0 }}
                                />
                              )}
                            </div>
                            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "1px" }}>
                              {formatVolume(coin.volume)}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "4px" }}>
                          <div
                            style={{
                              width: 40,
                              display: "flex",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Sparkline points={history} positive={isUp} />
                          </div>
                          <span style={{ 
                            fontSize: "clamp(11px, 2.5vw, 13px)", 
                            fontWeight: 700, 
                            color: "#ffffff", 
                            fontVariantNumeric: "tabular-nums" 
                          }}>
                            {loaded ? formatPrice(coin.price) : "—"}
                          </span>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "2px" }}>
                          <span
                            style={{
                              fontSize: "clamp(10px, 2.2vw, 12px)",
                              fontWeight: 700,
                              padding: "3px 6px",
                              borderRadius: "6px",
                              minWidth: "54px",
                              textAlign: "center",
                              fontVariantNumeric: "tabular-nums",
                              color: isUp ? "#4ade80" : "#f87171",
                              backgroundColor: isUp ? "rgba(74,222,128,0.14)" : "rgba(248,113,113,0.14)",
                            }}
                          >
                            {loaded ? `${isUp ? "+" : ""}${coin.changePct.toFixed(2)}%` : "—"}
                          </span>
                          <ChevronRight
                            size={12}
                            color="#475569"
                            style={{
                              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              flexShrink: 0,
                            }}
                          />
                        </div>
                      </div>

                      {isExpanded && (
                        <div
                          style={{
                            padding: "8px 10px 12px 38px",
                            marginBottom: "4px",
                            borderRadius: "10px",
                            background: "rgba(255,255,255,0.02)",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr 1fr",
                              gap: "6px",
                              marginBottom: "8px",
                            }}
                          >
                            <div>
                              <div style={{ fontSize: "9px", color: "#64748b" }}>Open</div>
                              <div style={{ fontSize: "11px", fontWeight: 600 }}>{formatPrice(coin.open)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: "9px", color: "#64748b" }}>High</div>
                              <div style={{ fontSize: "11px", fontWeight: 600, color: "#4ade80" }}>{formatPrice(coin.high)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: "9px", color: "#64748b" }}>Low</div>
                              <div style={{ fontSize: "11px", fontWeight: 600, color: "#f87171" }}>{formatPrice(coin.low)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: "9px", color: "#64748b" }}>Base Vol</div>
                              <div style={{ fontSize: "11px", fontWeight: 600 }}>{formatVolume(coin.baseVolume)}</div>
                            </div>
                          </div>

                          <div>
                            <div
                              style={{
                                position: "relative",
                                height: "4px",
                                borderRadius: "9999px",
                                background: "rgba(255,255,255,0.08)",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  left: `calc(${rangePct}% - 3px)`,
                                  top: "-2px",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "9999px",
                                  backgroundColor: isUp ? "#4ade80" : "#f87171",
                                  border: "2px solid #0b1220",
                                }}
                              />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                              <span style={{ fontSize: "9px", color: "#64748b" }}>Low {formatPrice(coin.low)}</span>
                              <span style={{ fontSize: "9px", color: "#64748b" }}>High {formatPrice(coin.high)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === "optional" && (
            <div className="text-center text-gray-400 py-6">
              No optional data yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Markets;